/**
 * TanStack Query hooks for tenant-scoped RegistrationField CRUD.
 *
 * Wraps the `/api/v1/registration-fields/` endpoints with a shared query
 * cache so that mutations in one component (e.g. the settings page) are
 * immediately visible in every other consumer (e.g. the condition builder).
 */

import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import type { PaginatedResponse } from '@/shared/types/api'

export type RegistrationFieldType =
  | 'TEXT'
  | 'RICH_TEXT'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'SELECT'
  | 'MULTI_SELECT'
  | 'DATE'
  | 'DATE_TIME'
  | 'EMAIL'
  | 'PHONE'
  | 'LINK'
  | 'MONEY'

export interface RegistrationField {
  uuid: string
  code_name: string
  display_name: string
  description: string
  field_type: RegistrationFieldType
  required: boolean
  configuration_data: Record<string, unknown>
  capture_step: string | null
  is_active: boolean
  order: number
}

/**
 * Catch-all predicate for "disclaimer / terms" registration fields.
 *
 * Any field whose `configuration_data.display_as === 'disclaimer'` is a
 * consent / acknowledgement surface (T&C, privacy, licence agreement, ...).
 * These are pulled out of their normal `capture_step` placement and rendered
 * at the very bottom of the Review step - the last thing a user acknowledges
 * before the final submit - regardless of tenant. Purely config-driven, so it
 * stays generic (no `tenant.slug`, no hardcoded `code_name`): a tenant opts a
 * field into this behaviour simply by marking it `display_as: 'disclaimer'`.
 */
export function isDisclaimerRegistrationField(field: {
  configuration_data?: Record<string, unknown> | null
}): boolean {
  const cd = field.configuration_data as Record<string, unknown> | undefined
  return cd?.display_as === 'disclaimer'
}

export interface RegistrationFieldWriteInput {
  code_name: string
  display_name: string
  description?: string
  field_type: RegistrationFieldType
  required?: boolean
  configuration_data?: Record<string, unknown>
  capture_step?: string | null
  is_active?: boolean
  order?: number
}

export const REG_FIELDS_KEY = ['registration-fields'] as const

// Tenant-scoped registration fields are bounded (typically <50 per tenant);
// we request a generous page size so existing consumers that expect a flat
// array continue to work without forcing pagination UI on settings pages.
//
// Exported as shared query options so the post-login cache warmer can prefetch
// this exact query without re-declaring the key/queryFn (no drift).
export function registrationFieldsQueryOptions() {
  return queryOptions<RegistrationField[]>({
    queryKey: REG_FIELDS_KEY,
    queryFn: () =>
      api
        .get<PaginatedResponse<RegistrationField>>('/v1/registration-fields/', {
          params: { page_size: 200 },
        })
        .then((r) => r.data.results),
    staleTime: 30_000,
  })
}

export function useRegistrationFields(options: { enabled?: boolean } = {}) {
  // The endpoint 404s for a tenant without the flag, so never request it there.
  const flagEnabled = useFeatureFlag(FLAGS.REGISTRATION_FIELDS)
  return useQuery<RegistrationField[]>({
    ...registrationFieldsQueryOptions(),
    enabled: (options.enabled ?? true) && flagEnabled,
  })
}

export function useCreateRegistrationField() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: RegistrationFieldWriteInput) =>
      api
        .post<RegistrationField>('/v1/registration-fields/', input)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: REG_FIELDS_KEY }),
  })
}

export function useUpdateRegistrationField() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, ...input }: { uuid: string } & RegistrationFieldWriteInput) =>
      api
        .put<RegistrationField>(`/v1/registration-fields/${uuid}/`, input)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: REG_FIELDS_KEY }),
  })
}

export function useDeleteRegistrationField() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/v1/registration-fields/${uuid}/`).then(() => undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: REG_FIELDS_KEY }),
  })
}
